import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface PageContextType {
  title: string;
  description: string;
  headerLeftActions: ReactNode | null;
  headerRightActions: ReactNode | null;
  setPageInfo: (title: string, description: string) => void;
  setHeaderActions: (
    headerLeftActions?: ReactNode | null,
    headerRightActions?: ReactNode | null
  ) => void;
}

const PageContext = createContext<PageContextType | undefined>(undefined);

interface PageProviderProps {
  children: ReactNode;
}

export const PageProvider: React.FC<PageProviderProps> = ({ children }) => {
  const [title, setTitle] = useState('DevSpott AI v2');
  const [description, setDescription] = useState('Plataforma de Multi-Agentes');
  const [headerLeftActions, setHeaderLeftActions] = useState<ReactNode | null>(
    null
  );
  const [headerRightActions, setHeaderRightActions] =
    useState<ReactNode | null>(null);

  const setPageInfo = useCallback((newTitle: string, newDescription: string) => {
    setTitle(newTitle);
    setDescription(newDescription);
  }, []);

  const setHeaderActions = useCallback((
    newHeaderLeftActions: ReactNode | null = null,
    newHeaderRightActions: ReactNode | null = null
  ) => {
    setHeaderLeftActions(newHeaderLeftActions);
    setHeaderRightActions(newHeaderRightActions);
  }, []);

  const value = useMemo(
    () => ({
      title,
      description,
      headerLeftActions,
      headerRightActions,
      setPageInfo,
      setHeaderActions,
    }),
    [
      title,
      description,
      headerLeftActions,
      headerRightActions,
      setPageInfo,
      setHeaderActions,
    ]
  );

  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
};

export const usePage = (): PageContextType => {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error('usePage must be used within a PageProvider');
  }
  return context;
};
